import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float, MeshDistortMaterial, Stars, Sparkles, Text } from "@react-three/drei";
import * as THREE from "three";
import { profile, services, techStack, projects, youtube } from "../data/content";

const LABEL_PROPS = {
  font: undefined,
  outlineWidth: 0.008,
  outlineColor: "#05050a",
  anchorX: "center",
  anchorY: "middle",
};

// The whole site is one continuous 3D corridor. Each "chapter" of the page
// sits at its own depth along -Z, and the camera dollies forward through it
// as the user scrolls, like flying through a level in a game.
const STAGE_DEPTH = 17;
const STAGE_COUNT = 7; // home, about, services, stack, projects, youtube, contact
const TOTAL_DEPTH = STAGE_DEPTH * (STAGE_COUNT - 1);
const zAt = (i) => -i * STAGE_DEPTH;

function useMouseParallax() {
  const mouse = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const handler = (e) => {
      mouse.current.x = e.clientX / window.innerWidth - 0.5;
      mouse.current.y = e.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener("pointermove", handler);
    return () => window.removeEventListener("pointermove", handler);
  }, []);
  return mouse;
}

function CameraRig({ progress }) {
  const { camera } = useThree();
  const mouse = useMouseParallax();

  useFrame((state, delta) => {
    const p = progress.get();
    const targetZ = 9 - p * (TOTAL_DEPTH + 9);
    const drift = Math.sin(p * Math.PI * 9);
    const targetX = Math.sin(p * Math.PI * 5) * 1.3 + mouse.current.x * 1.6;
    const targetY = Math.cos(p * Math.PI * 4) * 0.6 + drift * 0.15 - mouse.current.y * 0.9;

    const lerpF = 1 - Math.pow(0.0008, delta);
    camera.position.z = THREE.MathUtils.lerp(camera.position.z, targetZ, lerpF);
    camera.position.x = THREE.MathUtils.lerp(camera.position.x, targetX, lerpF);
    camera.position.y = THREE.MathUtils.lerp(camera.position.y, targetY, lerpF);

    camera.lookAt(camera.position.x * 0.4, camera.position.y * 0.4, camera.position.z - 14);
  });

  return null;
}

function TunnelRings() {
  const positions = useMemo(() => {
    const arr = [];
    for (let z = 5; z > -TOTAL_DEPTH - 14; z -= 5.5) arr.push(z);
    return arr;
  }, []);
  const group = useRef();
  useFrame((_, delta) => {
    if (group.current) group.current.rotation.z += delta * 0.025;
  });
  return (
    <group ref={group}>
      {positions.map((z, i) => (
        <mesh key={z} position={[0, 0, z]} rotation={[0, 0, i * 0.28]}>
          <torusGeometry args={[6.4, 0.014, 8, 64]} />
          <meshBasicMaterial
            color={i % 2 === 0 ? "#00e5ff" : "#a855f7"}
            transparent
            opacity={0.16}
          />
        </mesh>
      ))}
    </group>
  );
}

function HomeCore() {
  const core = useRef();
  const wire = useRef();
  useFrame((_, delta) => {
    if (core.current) {
      core.current.rotation.x += delta * 0.15;
      core.current.rotation.y += delta * 0.2;
    }
    if (wire.current) {
      wire.current.rotation.x -= delta * 0.08;
      wire.current.rotation.y -= delta * 0.12;
    }
  });
  return (
    <group position={[0, 0, 0]}>
      <Float speed={1.4} rotationIntensity={0.5} floatIntensity={1}>
        <mesh ref={core}>
          <icosahedronGeometry args={[1.7, 4]} />
          <MeshDistortMaterial
            color="#00e5ff"
            distort={0.38}
            speed={1.8}
            roughness={0.15}
            metalness={0.6}
            emissive="#a855f7"
            emissiveIntensity={0.25}
          />
        </mesh>
        <mesh ref={wire} scale={1.35}>
          <icosahedronGeometry args={[1.7, 1]} />
          <meshBasicMaterial color="#a855f7" wireframe transparent opacity={0.25} />
        </mesh>
      </Float>
      {[...Array(6)].map((_, i) => {
        const angle = (i / 6) * Math.PI * 2;
        return (
          <Float key={i} speed={2 + i * 0.2} floatIntensity={2} rotationIntensity={2}>
            <mesh position={[Math.cos(angle) * 3.6, Math.sin(angle * 1.3) * 0.9, Math.sin(angle) * 3.6]}>
              <octahedronGeometry args={[0.17, 0]} />
              <meshStandardMaterial
                color={i % 2 === 0 ? "#00ff9d" : "#00e5ff"}
                emissive={i % 2 === 0 ? "#00ff9d" : "#00e5ff"}
                emissiveIntensity={0.6}
              />
            </mesh>
          </Float>
        );
      })}
      <Text {...LABEL_PROPS} position={[0, -2.6, 0]} fontSize={0.34} color="#00e5ff" maxWidth={5}>
        {profile.name.toUpperCase()}
      </Text>
      <Text {...LABEL_PROPS} position={[0, -3.2, 0]} fontSize={0.16} color="#9497ab" maxWidth={5}>
        FOUNDER · ENGINEER CODEWALA
      </Text>
    </group>
  );
}

// ABOUT — the origin story: software engineer climbing into founder,
// represented as a "screen" (the desk job) with a rising path of steps
// leading up to a beacon (going independent), ringed by orbiting markers.
function AboutOrbits({ z }) {
  const g1 = useRef();
  const g2 = useRef();
  const screenRef = useRef();
  useFrame((_, delta) => {
    if (g1.current) g1.current.rotation.z += delta * 0.15;
    if (g2.current) g2.current.rotation.x += delta * 0.1;
    if (screenRef.current) screenRef.current.rotation.y = Math.sin(Date.now() * 0.0002) * 0.15;
  });

  const steps = useMemo(() => [...Array(5)].map((_, i) => i), []);

  return (
    <group position={[0, 0, z]}>
      <mesh ref={g1} rotation={[Math.PI / 2.4, 0, 0]}>
        <torusGeometry args={[3, 0.03, 16, 100]} />
        <meshBasicMaterial color="#00e5ff" transparent opacity={0.5} />
      </mesh>
      <mesh ref={g2} rotation={[0, Math.PI / 3, Math.PI / 6]}>
        <torusGeometry args={[2.2, 0.025, 16, 100]} />
        <meshBasicMaterial color="#a855f7" transparent opacity={0.4} />
      </mesh>

      {/* the "desk job" screen, code lines glowing on it */}
      <group ref={screenRef} position={[-2.1, -0.3, 0.4]} rotation={[0, 0.5, 0]}>
        <mesh>
          <boxGeometry args={[1.5, 1.0, 0.08]} />
          <meshStandardMaterial color="#0e0e17" emissive="#00e5ff" emissiveIntensity={0.18} />
        </mesh>
        <mesh position={[0, 0, 0.05]}>
          <planeGeometry args={[1.3, 0.82]} />
          <meshBasicMaterial color="#00e5ff" transparent opacity={0.12} />
        </mesh>
      </group>

      {/* rising steps toward the founder beacon */}
      {steps.map((i) => (
        <Float key={i} speed={1.2} floatIntensity={0.6}>
          <mesh position={[-0.6 + i * 0.75, -0.9 + i * 0.45, -0.2 - i * 0.15]}>
            <boxGeometry args={[0.4, 0.14, 0.4]} />
            <meshStandardMaterial
              color={i === steps.length - 1 ? "#00ff9d" : "#a855f7"}
              emissive={i === steps.length - 1 ? "#00ff9d" : "#a855f7"}
              emissiveIntensity={i === steps.length - 1 ? 0.7 : 0.3}
            />
          </mesh>
        </Float>
      ))}

      {[...Array(4)].map((_, i) => (
        <Float key={i} speed={1.6} floatIntensity={1.5}>
          <mesh position={[Math.cos(i * 1.6) * 3, Math.sin(i * 2) * 1.2, Math.sin(i * 1.6) * 3]}>
            <sphereGeometry args={[0.14, 16, 16]} />
            <meshStandardMaterial color="#00ff9d" emissive="#00ff9d" emissiveIntensity={0.7} />
          </mesh>
        </Float>
      ))}

      <Text {...LABEL_PROPS} position={[0, -2.4, 0.6]} fontSize={0.3} color="#eceef5" maxWidth={5} textAlign="center">
        Software Engineer → Founder
      </Text>
      <Text {...LABEL_PROPS} position={[0, -2.95, 0.6]} fontSize={0.15} color="#9497ab" maxWidth={5}>
        Now leading a team, 20+ products shipped
      </Text>
    </group>
  );
}

// SERVICES — six panels ringed around the flight path, each shaped and
// labeled after what's actually offered: phone (mobile), browser (web),
// server stack (backend), dashboard grid (admin), a cap (mentorship),
// and a play triangle (YouTube).
function ServicesLattice({ z }) {
  const group = useRef();
  useFrame((_, delta) => {
    if (group.current) group.current.rotation.y += delta * 0.06;
  });

  const iconFor = (i) => {
    switch (i) {
      case 0: // phone
        return (
          <mesh>
            <boxGeometry args={[0.62, 1.15, 0.08]} />
            <meshStandardMaterial color="#0e0e17" emissive="#00e5ff" emissiveIntensity={0.35} />
          </mesh>
        );
      case 1: // browser window
        return (
          <mesh>
            <boxGeometry args={[1.35, 0.9, 0.06]} />
            <meshStandardMaterial color="#0e0e17" emissive="#a855f7" emissiveIntensity={0.35} />
          </mesh>
        );
      case 2: // server stack
        return (
          <group>
            {[0, 1, 2].map((k) => (
              <mesh key={k} position={[0, -0.4 + k * 0.35, 0]}>
                <cylinderGeometry args={[0.55, 0.55, 0.24, 24]} />
                <meshStandardMaterial color="#0e0e17" emissive="#00ff9d" emissiveIntensity={0.3} />
              </mesh>
            ))}
          </group>
        );
      case 3: // dashboard grid
        return (
          <mesh>
            <planeGeometry args={[1.3, 1.3]} />
            <meshStandardMaterial
              color="#0e0e17"
              emissive="#00e5ff"
              emissiveIntensity={0.25}
              transparent
              opacity={0.7}
              side={THREE.DoubleSide}
            />
          </mesh>
        );
      case 4: // mentorship marker
        return (
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.62, 0.5, 4]} />
            <meshStandardMaterial color="#0e0e17" emissive="#a855f7" emissiveIntensity={0.4} />
          </mesh>
        );
      default: // play triangle
        return (
          <mesh rotation={[0, 0, -Math.PI / 2]}>
            <coneGeometry args={[0.5, 0.9, 3]} />
            <meshStandardMaterial color="#0e0e17" emissive="#ff0033" emissiveIntensity={0.5} />
          </mesh>
        );
    }
  };

  return (
    <group ref={group} position={[0, 0, z]}>
      {services.map((s, i) => {
        const angle = (i / services.length) * Math.PI * 2;
        const r = 3.4;
        const pos = [Math.cos(angle) * r, Math.sin(angle) * 1, Math.sin(angle) * r];
        return (
          <Float key={s.title} speed={1.3} floatIntensity={0.8} rotationIntensity={0.3}>
            <group position={pos} rotation={[0, -angle, 0]}>
              {iconFor(i)}
              <Text {...LABEL_PROPS} position={[0, -0.95, 0]} fontSize={0.13} color="#eceef5" maxWidth={2}>
                {s.title}
              </Text>
            </group>
          </Float>
        );
      })}
    </group>
  );
}

// STACK — the actual tools he reaches for daily, floating as labeled
// satellites pulled straight from the tech stack data.
function StackSatellites({ z }) {
  const group = useRef();
  useFrame((_, delta) => {
    if (group.current) group.current.rotation.y -= delta * 0.08;
  });

  const flatStack = useMemo(() => Object.values(techStack).flat(), []);

  const items = useMemo(
    () =>
      flatStack.slice(0, 10).map((name, i) => {
        const angle = (i / 10) * Math.PI * 2;
        const r = 2.6 + (i % 3) * 0.9;
        return {
          name,
          pos: [Math.cos(angle) * r, Math.sin(angle * 1.7) * 1.6, Math.sin(angle) * r],
          scale: 0.13 + (i % 3) * 0.03,
          color: ["#00e5ff", "#a855f7", "#00ff9d"][i % 3],
        };
      }),
    [flatStack]
  );

  return (
    <group ref={group} position={[0, 0, z]}>
      {items.map((it, i) => (
        <Float key={it.name} speed={1.4 + i * 0.08} floatIntensity={1.4} rotationIntensity={2.4}>
          <group position={it.pos}>
            <mesh scale={it.scale}>
              <boxGeometry args={[1, 1, 1]} />
              <meshStandardMaterial color={it.color} emissive={it.color} emissiveIntensity={0.45} />
            </mesh>
            <Text {...LABEL_PROPS} position={[0, -0.32, 0]} fontSize={0.12} color="#9497ab" maxWidth={2}>
              {it.name}
            </Text>
          </group>
        </Float>
      ))}
    </group>
  );
}

// PROJECTS — the actual shipped work, orbiting like satellites in a belt,
// each one shaped to hint at what it is and labeled with its real name.
function ProjectsBelt({ z }) {
  const group = useRef();
  useFrame((_, delta) => {
    if (group.current) group.current.rotation.y += delta * 0.05;
  });

  const shapeFor = (i, color) => {
    switch (i % 5) {
      case 0: // classified platform — a tag
        return (
          <mesh rotation={[0, 0, Math.PI / 4]}>
            <octahedronGeometry args={[0.5, 0]} />
            <meshStandardMaterial color="#13131f" emissive={color} emissiveIntensity={0.35} />
          </mesh>
        );
      case 1: // music app — a spinning disc
        return (
          <mesh rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.5, 0.16, 16, 40]} />
            <meshStandardMaterial color="#13131f" emissive={color} emissiveIntensity={0.35} />
          </mesh>
        );
      case 2: // job portal — a briefcase
        return (
          <mesh>
            <boxGeometry args={[1.05, 0.7, 0.4]} />
            <meshStandardMaterial color="#13131f" emissive={color} emissiveIntensity={0.3} />
          </mesh>
        );
      case 3: // EV bike — a wheel
        return (
          <mesh>
            <torusGeometry args={[0.5, 0.09, 12, 32]} />
            <meshStandardMaterial color="#13131f" emissive={color} emissiveIntensity={0.4} />
          </mesh>
        );
      default: // trailer app — a clapperboard-ish slab
        return (
          <mesh rotation={[0.15, 0, 0]}>
            <boxGeometry args={[1.1, 0.75, 0.1]} />
            <meshStandardMaterial color="#13131f" emissive={color} emissiveIntensity={0.35} />
          </mesh>
        );
    }
  };

  return (
    <group ref={group} position={[0, 0, z]}>
      {projects.map((p, i) => {
        const angle = (i / projects.length) * Math.PI * 2;
        const r = 3.8;
        const color = i % 2 === 0 ? "#00e5ff" : "#a855f7";
        return (
          <Float key={p.title} speed={1.2} floatIntensity={1} rotationIntensity={1}>
            <group position={[Math.cos(angle) * r, Math.sin(angle * 2) * 0.6, Math.sin(angle) * r]} rotation={[0.4, angle, 0]}>
              {shapeFor(i, color)}
              <Text {...LABEL_PROPS} position={[0, -0.75, 0]} fontSize={0.12} color="#eceef5" maxWidth={2} textAlign="center">
                {p.title}
              </Text>
            </group>
          </Float>
        );
      })}
    </group>
  );
}

function YoutubeBeacon({ z }) {
  const sphere = useRef();
  const ring = useRef();
  useFrame((_, delta) => {
    if (sphere.current) sphere.current.rotation.y += delta * 0.2;
    if (ring.current) ring.current.rotation.z += delta * 0.3;
  });
  return (
    <group position={[0, 0, z]}>
      <mesh ref={sphere}>
        <sphereGeometry args={[1.3, 32, 32]} />
        <meshStandardMaterial color="#ff0033" emissive="#ff0033" emissiveIntensity={0.5} roughness={0.3} />
      </mesh>
      <mesh ref={ring} rotation={[Math.PI / 2.5, 0, 0]}>
        <torusGeometry args={[2.4, 0.05, 16, 100]} />
        <meshBasicMaterial color="#ff5577" transparent opacity={0.6} />
      </mesh>
      <Text {...LABEL_PROPS} position={[0, -2.1, 0]} fontSize={0.32} color="#ff5577" maxWidth={5}>
        {youtube.channelName}
      </Text>
      <Text {...LABEL_PROPS} position={[0, -2.65, 0]} fontSize={0.15} color="#9497ab" maxWidth={5}>
        {youtube.subs} subscribers · weekly tutorials
      </Text>
    </group>
  );
}

function ContactPortal({ z }) {
  const group = useRef();
  useFrame((_, delta) => {
    if (group.current) group.current.rotation.z += delta * 0.2;
  });
  const rings = [0, 1, 2, 3, 4];
  return (
    <group ref={group} position={[0, 0, z]}>
      {rings.map((i) => (
        <mesh key={i} position={[0, 0, -i * 1.6]} rotation={[0, 0, i]}>
          <torusGeometry args={[2.6 - i * 0.35, 0.04, 16, 80]} />
          <meshBasicMaterial color={i % 2 === 0 ? "#00e5ff" : "#a855f7"} transparent opacity={0.5 - i * 0.06} />
        </mesh>
      ))}
      <pointLight color="#ffffff" intensity={3} distance={12} position={[0, 0, -8]} />
      <Text {...LABEL_PROPS} position={[0, 1.6, -2]} fontSize={0.3} color="#eceef5" maxWidth={5}>
        Let's Build Something
      </Text>
    </group>
  );
}

function Scene({ progress }) {
  return (
    <>
      <color attach="background" args={["#05050a"]} />
      <fog attach="fog" args={["#05050a", 8, 42]} />
      <ambientLight intensity={0.45} />
      <pointLight position={[6, 6, 6]} intensity={1.1} color="#00e5ff" />
      <pointLight position={[-6, -4, -10]} intensity={0.9} color="#a855f7" />

      <CameraRig progress={progress} />
      <TunnelRings />
      <Stars radius={60} depth={60} count={2200} factor={2.2} saturation={0} fade speed={0.4} />
      <Sparkles
        count={100}
        scale={[16, 8, TOTAL_DEPTH + 30]}
        size={1.6}
        speed={0.3}
        color="#00e5ff"
        opacity={0.32}
        position={[0, 0, -TOTAL_DEPTH / 2]}
      />

      <HomeCore />
      <AboutOrbits z={zAt(1)} />
      <ServicesLattice z={zAt(2)} />
      <StackSatellites z={zAt(3)} />
      <ProjectsBelt z={zAt(4)} />
      <YoutubeBeacon z={zAt(5)} />
      <ContactPortal z={zAt(6)} />
    </>
  );
}

export default function Experience({ progress }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 0 }}>
      <Canvas camera={{ position: [0, 0, 9], fov: 50 }} dpr={[1, 1.6]} gl={{ antialias: true }}>
        <Scene progress={progress} />
      </Canvas>
    </div>
  );
}
